/** App chrome: brand, nav, and the sign-out control. */
import { Link, NavLink, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../store/auth";
import { Logo } from "./Logo";
import { IconCard, IconList, IconLogout, IconMic, EarningCardIcon, MicrophoneIcon } from "./Icons";

interface LayoutProps {
  children: ReactNode;
  /** Hidden during onboarding, where nav would be a dead end. */
  showNav?: boolean;
  /**
   * The contributor's own account controls. Off in the back office, which is
   * a different surface with its own sign-in — showing both put two sign-out
   * buttons on screen that did different things.
   */
  showAccount?: boolean;
  title?: string;
}

function initials(name?: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const letters = ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? "")).toUpperCase();
  return letters || "•";
}

export function Layout({ children, showNav = true, showAccount = true, title }: LayoutProps) {
  const contributor = useAuth((state) => state.contributor);
  const logout = useAuth((state) => state.logout);
  const navigate = useNavigate();

  async function handleSignOut() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header-inner">
          <Logo to={contributor ? "/record" : "/"} />
          {title && !showNav && <span className="page-title">{title}</span>}

          {showNav && contributor && (
            <nav className="app-links" aria-label="Contributor">
              <NavLink to="/record" className={({ isActive }) => (isActive ? "active nav-word" : "nav-word")}>
                <MicrophoneIcon /> Record
              </NavLink>
              <NavLink to="/log" className={({ isActive }) => (isActive ? "active nav-word" : "nav-word")}>
                <IconList width={16} height={16} /> My Log
              </NavLink>
              <NavLink to="/earnings" className={({ isActive }) => (isActive ? "active nav-word" : "nav-word")}>
                <EarningCardIcon /> Earnings
              </NavLink>
            </nav>
          )}

          {showAccount && contributor && (
            <div className="app-links">
              <NavLink to="/account" className="avatar" aria-label="Account">
                {initials(contributor.name)}
              </NavLink>
              <button type="button" className="signout" onClick={handleSignOut}>
                <IconLogout />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </header>
      <main>{children}</main>
      {showNav && contributor && (
        <footer className="app-footer">
          <div className="app-footer-inner">
            <span>© 2026 Inputry</span>
            <nav aria-label="Footer">
              <Link to="/terms">Contributor Terms</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/how-it-works">Help</Link>
              <Link to="/contact">Contact</Link>
            </nav>
          </div>
        </footer>
      )}
      {showNav && contributor && (
        <nav className="tabbar" aria-label="Contributor">
          <NavLink to="/record" className={({ isActive }) => (isActive ? "active" : "")}>
            <IconMic width={18} height={18} />
            Record
          </NavLink>
          <NavLink to="/log" className={({ isActive }) => (isActive ? "active" : "")}>
            <IconList width={18} height={18} />
            My Log
          </NavLink>
          <NavLink to="/earnings" className={({ isActive }) => (isActive ? "active" : "")}>
            <IconCard width={18} height={18} />
            Earnings
          </NavLink>
        </nav>
      )}
    </div>
  );
}
